import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Label, Card, CardContent } from '@tyndo/ui-kit';
import { resolveOidcConfig, beginLogin } from '../auth/oidc';
import { recordError } from '../observability/telemetry';
import {
  login as nativeLogin,
  resendVerification,
  storeSessionToken,
  AuthApiError,
} from '../auth/nativeAuth';

export default function LoginPage() {
  const navigate = useNavigate();

  // Primary path: native email + password.
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // When login fails specifically because the email is unverified, offer a
  // one-click resend rather than a dead-end error.
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendNotice, setResendNotice] = useState<string | null>(null);
  const [resending, setResending] = useState(false);

  // Secondary / ops paths.
  const [oauthError, setOauthError] = useState<string | null>(null);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setNeedsVerification(false);
    setResendNotice(null);

    if (!email.trim() || !password) {
      setError('Please enter your email and password');
      return;
    }

    setLoading(true);
    try {
      const session = await nativeLogin({ email: email.trim(), password });
      storeSessionToken(session.token);
      navigate('/');
    } catch (err) {
      // The gateway returns ONE generic 401 detail — "Invalid credentials or
      // unverified account." — for both causes, deliberately, so a stranger
      // cannot probe which addresses exist.
      //
      // So the client CANNOT know which happened, and must not pretend to. The
      // previous code tested that detail with /verif/i — but the generic string
      // always contains "unverified", so the test was always true: every wrong
      // password produced a confident "Your email is not verified yet", and the
      // "Invalid email or password" branch was unreachable against a real
      // gateway. A user with a typo'd password was told their account was
      // unverified and pointed at a resend button; on a deployment with SMTP
      // off, that is a dead end that looks exactly like a broken account.
      //
      // Report the ambiguity the server actually expressed, and offer resend as
      // an OPTION rather than as a diagnosis.
      setNeedsVerification(true);
      setError(
        'We could not sign you in. Check your email and password — if the '
          + 'account is new, its email address may still need verifying.'
      );
      recordError('auth.native_login_failed', err, {
        statusCode: err instanceof AuthApiError ? err.statusCode : undefined,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendNotice(null);
    setResending(true);
    try {
      const result = await resendVerification(email.trim());
      setResendNotice(result.message);
    } catch (err) {
      // Enumeration-safe endpoint always 200s; only a network error lands here.
      setResendNotice('Could not send the verification email. Please try again.');
      recordError('auth.resend_verification_failed', err, {});
    } finally {
      setResending(false);
    }
  };

  const handleKeycloakLogin = async () => {
    // Authorization Code + PKCE + state. beginLogin() mints the PKCE
    // verifier/challenge + CSRF state, stashes them, and redirects to the
    // authorize endpoint; the /auth/callback route completes the exchange.
    try {
      setOauthError(null);
      await beginLogin(resolveOidcConfig(), '/');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not start sign-in';
      setOauthError(message);
      recordError('auth.begin_login_failed', err, {});
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        // Aurora hero gradient — soft indigo wash over the cool-slate base,
        // referencing the canonical gradient token so the same recipe lights up
        // every brand surface (login, marketing splash, KPI hero).
        background: 'var(--gradient-hero)',
      }}
    >
      <div className="w-full max-w-md">
        <Card variant="elevated" className="shadow-[var(--shadow-overlay)] rounded-2xl border-0">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-primary rounded-xl mb-4 shadow-[var(--shadow-md)]">
                <span className="text-2xl font-bold text-primary-foreground">T</span>
              </div>
              <h1 className="text-3xl font-semibold tracking-tight text-foreground">Tyndo</h1>
              <p className="t-eyebrow mt-3">Manufacturing Operating System</p>
            </div>

            {/* Primary path: email + password login */}
            <form onSubmit={handleLogin} className="space-y-4">
              {error && (
                <div className="bg-alarm-critical/10 border border-alarm-critical/30 text-alarm-critical px-4 py-3 rounded-md text-sm">
                  {error}
                  {needsVerification && (
                    <Button
                      variant="link"
                      type="button"
                      onClick={handleResendVerification}
                      disabled={resending}
                      className="mt-2 block h-auto p-0 underline font-medium"
                    >
                      {resending ? 'Sending…' : 'Resend verification email'}
                    </Button>
                  )}
                </div>
              )}

              {resendNotice && (
                <div className="bg-alarm-advisory/10 border border-alarm-advisory/30 text-foreground px-4 py-3 rounded-md text-sm">
                  {resendNotice}
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="login-email">Email</Label>
                <Input
                  id="login-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  disabled={loading}
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="login-password">Password</Label>
                  <Button
                    variant="link"
                    type="button"
                    onClick={() => navigate('/auth/forgot')}
                    className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground underline"
                  >
                    Forgot password?
                  </Button>
                </div>
                <Input
                  id="login-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  disabled={loading}
                />
              </div>

              <Button type="submit" size="lg" disabled={loading} className="w-full">
                {loading ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>

            {/* Sign up — repurposes the old "Start free trial" CTA to open the
                self-serve registration page. */}
            <p className="text-sm text-muted-foreground text-center mt-4">
              New to Tyndo?{' '}
              <Button
                variant="link"
                type="button"
                onClick={() => navigate('/auth/signup')}
                className="h-auto p-0 text-foreground font-medium underline"
              >
                Start free trial
              </Button>
            </p>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-card text-muted-foreground">Or</span>
              </div>
            </div>

            {/* Secondary: Keycloak SSO */}
            {oauthError && (
              <div className="bg-alarm-critical/10 border border-alarm-critical/30 text-alarm-critical px-4 py-3 rounded-md text-sm mb-4">
                {oauthError}
              </div>
            )}
            <Button
              onClick={handleKeycloakLogin}
              variant="outline"
              size="lg"
              className="w-full"
            >
              Sign in with Keycloak
            </Button>

            {/* Help Text */}
            <p className="text-xs text-muted-foreground text-center mt-6">
              Contact your administrator if you don't have login credentials
            </p>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-foreground/60 text-xs mt-6">
          <p>&copy; {new Date().getFullYear()} Tyndo by XarvisLabs. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
